// 파일 미리보기 — 채팅·관리자 화면 어디서든 openFileViewer(...) 한 줄로 연다.
// 실제 창은 App 에 한 번 놓인 <FileViewerHost/> 가 그린다 (props 를 여러 겹 내려보내지 않기 위해 이벤트로 연결).

const EXT_KIND = {};
const add = (kind, exts) => exts.split(' ').forEach(ext => { EXT_KIND[ext] = kind; });
add('image', 'png jpg jpeg gif webp avif bmp ico jfif');
add('svg', 'svg'); // 서버가 보안상 첨부파일로만 내주므로 받아서 그린다 (<img> 로 그리면 스크립트는 실행되지 않음)
add('video', 'mp4 webm mov m4v ogv');
add('audio', 'mp3 wav ogg oga m4a flac aac opus');
add('pdf', 'pdf');
add('markdown', 'md markdown mdown');
add('csv', 'csv tsv');
add('docx', 'docx docm dotx');
add('xlsx', 'xlsx xlsm xltx');
add('pptx', 'pptx pptm ppsx');
add('hwpx', 'hwpx');
add('zip', 'zip');
// 글자로 된 파일 (코드·설정·로그·PLC 소스 등) — 내용을 그대로 보여준다
add('text', 'txt text log ini cfg conf env properties json jsonc xml yml yaml toml html htm css scss js jsx ts tsx mjs cjs '
    + 'py java c h cpp hpp cc cs go rs rb php sh bash zsh bat cmd ps1 psm1 sql kt swift lua pl r m vb vbs '
    + 'st scl awl il ld iec gitignore dockerfile makefile srt vtt nfo');

// 미리보기는 못 하지만 이유를 알려줄 형식
const UNSUPPORTED_REASON = {
    hwp: '한글(.hwp) 옛 형식은 브라우저에서 열 수 없습니다. 한글에서 .hwpx 로 저장하면 미리보기가 됩니다.',
    doc: 'Word 97-2003(.doc) 형식은 미리보기를 지원하지 않습니다. .docx 는 됩니다.',
    xls: 'Excel 97-2003(.xls) 형식은 미리보기를 지원하지 않습니다. .xlsx 는 됩니다.',
    ppt: 'PowerPoint 97-2003(.ppt) 형식은 미리보기를 지원하지 않습니다. .pptx 는 됩니다.',
};

export const extOf = (name = '') => {
    const base = String(name).split(/[?#]/)[0].split('/').pop().toLowerCase();
    if (!base.includes('.')) return base; // Dockerfile, Makefile
    return base.slice(base.lastIndexOf('.') + 1);
};

export const previewKindOf = (name, mimetype = '') => {
    const byExt = EXT_KIND[extOf(name)];
    if (byExt) return byExt;
    if (mimetype.startsWith('image/')) return 'image';
    if (mimetype.startsWith('video/')) return 'video';
    if (mimetype.startsWith('audio/')) return 'audio';
    if (mimetype === 'application/pdf') return 'pdf';
    if (mimetype.startsWith('text/')) return 'text';
    return null;
};

export const unsupportedReason = (name) => UNSUPPORTED_REASON[extOf(name)] || null;
export const canPreview = (name, mimetype) => !!previewKindOf(name, mimetype);

// 다운로드 링크(/api/upload-download/…?name=원래이름)에서 원래 파일명을 꺼낸다
export const nameFromUrl = (url, fallback = '') => {
    try {
        const u = new URL(url, window.location.origin);
        const q = u.searchParams.get('name');
        if (q) return q;
        const last = decodeURIComponent(u.pathname.split('/').pop() || '');
        return last.replace(/^\d{13}-[a-f0-9]{8}-/, '') || fallback;
    } catch { return fallback; }
};

const EVENT = 'bc:file-viewer';

/**
 * files: [{ url, name, downloadUrl?, mimetype? }] (한 개여도 배열), index: 처음 보여줄 위치
 * 사진·영상만 있으면 기존 사진 뷰어(Lightbox)로, 그 밖에는 파일 뷰어로 연다.
 */
export const openFileViewer = (files, index = 0) => {
    const list = (Array.isArray(files) ? files : [files]).filter(f => f && f.url);
    if (!list.length) return;
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { files: list, index: Math.max(0, Math.min(index, list.length - 1)) } }));
};

export const onFileViewerOpen = (handler) => {
    const listener = (e) => handler(e.detail);
    window.addEventListener(EVENT, listener);
    return () => window.removeEventListener(EVENT, listener);
};

const CHAT_FILE_PATH = /^\/(uploads|admin_uploads)\/|^\/api\/(upload-download|admin-download)\//;
const isChatFileUrl = (url) => {
    try { return CHAT_FILE_PATH.test(new URL(url, window.location.origin).pathname) && new URL(url, window.location.origin).origin === window.location.origin; }
    catch { return false; }
};

/**
 * 메시지 본문(HTML) 클릭 처리: 우리 서버 파일 링크(📎)나 옛 방식으로 본문에 박힌 사진을 누르면
 * 내려받는 대신 미리보기로 연다. 미리보기가 안 되는 형식(.hwp, .exe 등)은 원래대로 내려받게 둔다.
 * 연 경우 true.
 */
export const openFromMessageClick = (e) => {
    if (e.defaultPrevented || e.button > 0 || e.ctrlKey || e.metaKey || e.shiftKey) return false;
    const img = e.target.closest?.('img');
    if (img && !img.closest('a') && isChatFileUrl(img.getAttribute('src'))) {
        const src = img.getAttribute('src');
        const name = img.getAttribute('alt') || nameFromUrl(src, '사진');
        e.preventDefault();
        openFileViewer({ url: src, name, mimetype: 'image/*' });
        return true;
    }
    const a = e.target.closest?.('a[href]');
    if (!a) return false;
    const href = a.getAttribute('href');
    if (!isChatFileUrl(href)) return false;
    const label = a.textContent.replace(/^📎\s*/, '').trim();
    const name = nameFromUrl(href, label) || label;
    if (!canPreview(name) && !canPreview(label)) return false;
    e.preventDefault();
    openFileViewer({ url: href, downloadUrl: href, name: canPreview(name) ? name : label });
    return true;
};
